import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import type { CurrentUser, LoginRequest } from '@/api/types'

/**
 * Streama CMS · Auth store
 *
 * Holds the JWT and the current user. Persists to localStorage so
 * a page reload doesn't bounce the user back to /login.
 *
 * The token is a plain string (HS256 JWT, 8h TTL per Sprint 1
 * backend). We extract the `exp` claim client-side to schedule
 * refreshes; the signature is NOT verified client-side (the backend
 * is the only authority).
 *
 * Privilege checks: the store exposes `hasPrivilege(name)` and a
 * reactive `privileges` set. UI components and route guards use
 * these to gate actions and routes.
 */

const STORAGE_KEY = 'streama-cms.auth'
const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000 // refresh 5 min before exp

interface PersistedState {
  token: string
  expiresAt: string
  user: CurrentUser
}

interface JwtClaims {
  sub?: string
  exp?: number // seconds since epoch
  privileges?: string[]
}

export const useAuthStore = defineStore('auth', () => {
  // ---- state -----------------------------------------------------
  const token = ref<string | null>(null)
  const expiresAt = ref<string | null>(null)
  const user = ref<CurrentUser | null>(null)

  // ---- hydrate from localStorage at boot -------------------------
  hydrate()

  // ---- getters ---------------------------------------------------
  const isAuthenticated = computed(() => {
    if (!token.value || !expiresAt.value) return false
    // Treat as authenticated only while the token has time left.
    return Date.parse(expiresAt.value) > Date.now()
  })

  const privileges = computed<Set<string>>(() => {
    return new Set(user.value?.privileges ?? [])
  })

  const userName = computed(() => user.value?.userName ?? null)

  // ---- actions ---------------------------------------------------

  /**
   * Sign in with username + password. On success, sets state and
   * persists. Throws on failure — caller (the LoginView form)
   * surfaces the error to the user.
   */
  async function login(req: LoginRequest): Promise<void> {
    const res = await authApi.login(req)
    setSession(res.token, res.expiresAt, res.user)
  }

  /**
   * Hit the backend logout endpoint (best-effort) and clear local
   * state. Always succeeds from the caller's perspective.
   */
  async function logout(): Promise<void> {
    if (token.value) {
      await authApi.logout()
    }
    clear()
  }

  /**
   * Clear local state without touching the server. Used by the
   * 401-handling axios interceptor (the token is already invalid;
   * no point calling logout) and by the explicit logout() above.
   */
  function clear(): void {
    token.value = null
    expiresAt.value = null
    user.value = null
    localStorage.removeItem(STORAGE_KEY)
  }

  /**
   * Privilege predicate. Returns true if the current user holds
   * the named authority. Use the `useAuthority` composable for
   * reactive access in templates.
   */
  function hasPrivilege(name: string): boolean {
    return privileges.value.has(name)
  }

  /**
   * Refresh the token if expiry is near. Best-effort — logs and
   * swallows errors. Schedules itself; called from main.ts at boot.
   */
  async function refreshIfNeeded(): Promise<void> {
    if (!token.value || !expiresAt.value) return
    const msUntilExpiry = Date.parse(expiresAt.value) - Date.now()
    if (msUntilExpiry > TOKEN_REFRESH_LEAD_MS) return

    try {
      const res = await authApi.refresh({ token: token.value })
      setSession(res.token, res.expiresAt, res.user)
    } catch {
      // Backend may not implement /refresh yet (UI Sprint 1 spec).
      // The token will continue working until it actually expires;
      // at that point the 401 interceptor handles the logout.
    }
  }

  // ---- internal --------------------------------------------------

  function setSession(t: string, exp: string, u: CurrentUser): void {
    token.value = t
    expiresAt.value = exp
    user.value = u
    persist()
  }

  function persist(): void {
    if (!token.value || !expiresAt.value || !user.value) return
    const payload: PersistedState = {
      token: token.value,
      expiresAt: expiresAt.value,
      user: user.value,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }

  function hydrate(): void {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as PersistedState
      // Validate the persisted shape minimally — discard if missing
      // any required field rather than booting the app into a half
      // state.
      if (
        typeof parsed.token === 'string' &&
        typeof parsed.expiresAt === 'string' &&
        parsed.user &&
        typeof parsed.user.userName === 'string'
      ) {
        // If already expired at hydrate time, drop it.
        if (Date.parse(parsed.expiresAt) <= Date.now()) {
          localStorage.removeItem(STORAGE_KEY)
          return
        }
        token.value = parsed.token
        expiresAt.value = parsed.expiresAt
        user.value = parsed.user
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  /**
   * Decode the JWT payload without verifying the signature. Used
   * only as a fallback when the backend's /me endpoint is not yet
   * implemented — letting us reconstruct privileges from the token
   * itself.
   *
   * Public signature ignored on purpose: client-side verification
   * provides no security; the backend is the authority.
   */
  function decodeJwtClaims(jwt: string): JwtClaims | null {
    try {
      const segments = jwt.split('.')
      if (segments.length !== 3) return null
      const payloadB64 = segments[1]
      if (!payloadB64) return null
      // Base64URL decode (replace URL-safe chars then atob)
      const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(json) as JwtClaims
    } catch {
      return null
    }
  }

  return {
    // state
    token,
    expiresAt,
    user,
    // getters
    isAuthenticated,
    privileges,
    userName,
    // actions
    login,
    logout,
    clear,
    hasPrivilege,
    refreshIfNeeded,
    decodeJwtClaims,
  }
})
